import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import React, { Children, forwardRef, FunctionComponent } from "react";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import Grid, { GridProps } from "./Grid";
import GridItemGrow from "./GridItemGrow";
import clsx from "clsx";

const GRID_CONTAINER_COLUMN_CLASS = "GridContainerColumn";

export interface GridContainerProps extends Omit<GridProps, "container"> {
	/** indicates container will grow to fill its parent container */
	grow?: boolean;

	/** indicates all children should grow to fill container */
	growAllChildren?: boolean;

	/** index of child that should grow to fill this container */
	growChild?: number;

	/** indicates if container should scroll in its direction (i.e. if column, then "hidden auto") */
	scroll?: boolean;

	/** spacing around column */
	spacingAround?: number;

	/** spacing around left and right*/
	spacingAroundX?: number;

	/** spacing around top and bottom */
	spacingAroundY?: number;

	/** indicates if you want the standard border on container */
	standardBorder?: boolean;
}

const StyledGrid = styled(Grid)({
	[`&.${GRID_CONTAINER_COLUMN_CLASS}`]: {
		"& > .MuiGrid-item": {
			width: "100%",
		},
	},

});

const GridContainer: FunctionComponent<GridContainerProps> = forwardRef((props, ref) => {
	const { bgcolor, children, className, grow, growAllChildren, growChild, scroll, spacingAround, spacingAroundX, spacingAroundY, standardBorder, ...otherProps } = props;
	const childrenArray = Children.toArray(children);
	const addBox = grow || spacingAround || spacingAroundX || spacingAroundY || standardBorder;
	const boxDependentClassName = addBox ? null : className;
	const columnDependentClassName = (props.direction == "column" ? GRID_CONTAINER_COLUMN_CLASS : null);

	const grid = (
		<StyledGrid
			bgcolor={addBox ? null : bgcolor}
			className={clsx(boxDependentClassName, columnDependentClassName)}
			container
			ref={ref}
			{...otherProps}
		>
			{
				childrenArray.map((Child, i) =>
					(growAllChildren || (i === growChild)) ? (
						<GridItemGrow key={i}>
							{Child}
						</GridItemGrow>
					)
						: (
							<Grid
								flexShrink={0}
								item
								key={i}
							>
								{Child}
							</Grid>
						))
			}
		</StyledGrid>
	);

	if (addBox) {
		return (
			<Box
				bgcolor={bgcolor}
				border={standardBorder ? STANDARD_BORDER_STYLE : null}
				borderRadius={standardBorder ? 1 : null}
				className={className}
				flexGrow={grow ? 1 : null}
				padding={spacingAround}
				paddingX={spacingAroundX}
				paddingY={spacingAroundY}
			>
				{grid}
			</Box>
		);
	}

	return grid;
});



export interface GridColumnProps extends GridContainerProps {

}

export const GridColumn: FunctionComponent<GridColumnProps> = forwardRef((props, ref) => {
	const { overflow, scroll, ...otherProps } = props;

	return (
		<GridContainer
			direction="column"
			overflow={props.grow ? "hidden" : (scroll ? "hidden auto" : overflow)}
			ref={ref}
			{...otherProps}
		/>
	);
});

export interface GridRowProps extends GridContainerProps {

}

export const GridRow: FunctionComponent<GridRowProps> = forwardRef((props, ref) => {
	const { overflow, scroll, ...otherProps } = props;

	return (
		<GridContainer
			overflow={props.grow ? "hidden" : (scroll ? "auto hidden" : overflow)}
			ref={ref}
			{...otherProps}
		/>
	);
});
